import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { extractErrorMessage } from '@/shared/api/errors';
import { useLegacyClientsList } from '@/entities/legacy-clients';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { formatDate } from '@/shared/utils/date';
import type { ApiLegacyClientItem } from '@/shared/types/api/legacy-clients';

const PAGE_SIZE = 20;

export const WidgetsLegacyClientsMain = () => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = useLegacyClientsList(params);

  const columns = useMemo<ColumnDef<ApiLegacyClientItem>[]>(
    () => [
      {
        accessorKey: 'cargo_id_code',
        header: t('legacyClients.list.columns.cargoIdCode'),
        cell: (info) => (
          <span className="font-mono text-xs text-ink">
            {info.row.original.cargo_id_code?.trim() || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'full_name',
        header: t('legacyClients.list.columns.fullName'),
        cell: (info) =>
          info.row.original.full_name?.trim() || (
            <span className="text-ink-faint">—</span>
          ),
      },
      {
        accessorKey: 'phone_number',
        header: t('legacyClients.list.columns.phone'),
        cell: (info) =>
          info.row.original.phone_number?.trim() || (
            <span className="text-ink-faint">—</span>
          ),
      },
      {
        accessorKey: 'username',
        header: t('legacyClients.list.columns.username'),
        cell: (info) => {
          const u = info.row.original.username?.trim();
          if (!u) return <span className="text-ink-faint">—</span>;
          return <span className="text-ink">@{u}</span>;
        },
      },
      {
        accessorKey: 'telegram_name',
        header: t('legacyClients.list.columns.telegramName'),
        cell: (info) =>
          info.row.original.telegram_name?.trim() || (
            <span className="text-ink-faint">—</span>
          ),
      },
      {
        accessorKey: 'mongo_created_at',
        header: t('legacyClients.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.mongo_created_at),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('legacyClients.list.title')}
        subtitle={t('legacyClients.list.subtitle')}
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('legacyClients.list.searchPlaceholder')}
        />

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('legacyClients.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={query.data?.results ?? []}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('legacyClients.list.emptyTitle')}
              emptyDescription={t('legacyClients.list.emptyDescription')}
              emptyIcon={<Archive className="h-8 w-8" aria-hidden />}
              getRowId={(row) => String(row.id)}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={query.data?.count ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </>
  );
};
